import { Type } from '@angular/core';

import { IMyBetsListRequest } from '../services/my-bets-api.service';

export interface IFilterConstraint<T> {
    title: string;
    reduce(input: T): T;
}

export class MyBetsStateFilterContstraint implements IFilterConstraint<IMyBetsListRequest> {
    title: string;

    /**
     * Create Bet State filter for my bets request
     *
     * @param title - Localized title eg, (Gewonnen/Спечелен)
     * @param state - State we want to filter (WON, LOST, OTHERS)
     */
    constructor(
        title: string,
        public state: string,
    ) {
        this.title = title;
    }

    reduce(input: IMyBetsListRequest): IMyBetsListRequest {
        return { ...input, outcome: this.state };
    }
}

export class MyBetsDateRangeFilterContstraint implements IFilterConstraint<IMyBetsListRequest> {
    title: string;

    /**
     * Create date range filter for my bets request
     *
     * @param title - title to display eg ( `${fromDate} - ${toDate}` )
     * @param fromDate - from date to filter the betslips ( user local date)
     * @param toDate - to date to filter the betslips ( user local date)
     */
    constructor(
        title: string,
        public fromDate: Date,
        public toDate: Date,
    ) {
        this.title = title;
    }

    private convert = (date: Date | null): string => {
        if (date) {
            return date.toISOString();
        }

        return '';
    };

    private addDay(date: Date): Date {
        const copy = new Date(date);
        copy.setDate(copy.getDate() + 1);

        return copy;
    }

    reduce(input: IMyBetsListRequest): IMyBetsListRequest {
        return { ...input, fromDate: this.convert(this.fromDate), toDate: this.convert(this.addDay(this.toDate)) };
    }
}

export class MyBetsMigrationFilterContstraint implements IFilterConstraint<IMyBetsListRequest> {
    title: string;
    /**
     * Create migration filter for my bets request
     *
     * @param title - Localized title
     * @param toDate - migration date to filter the betslips ( user local date)
     * @param associatedAccount - account in .com before migration
     * @param fromDate -	from date to filter the betslips ( user local date)
     */
    constructor(
        title: string,
        public toDate: Date,
        public associatedAccount: string,
        public fromDate?: Date,
    ) {
        this.title = title;
    }

    private convert = (date: Date | null): string => {
        if (date) {
            return date.toISOString();
        }

        return '';
    };

    reduce(input: IMyBetsListRequest): IMyBetsListRequest {
        const response = {
            ...input,
            toDate: this.convert(this.toDate),
            associatedAccount: this.associatedAccount,
        };

        if (this.fromDate) {
            response.fromDate = this.convert(this.fromDate);
        }

        return response;
    }
}

export class MyBetsFilterConstraint {
    constraints: IFilterConstraint<IMyBetsListRequest>[] = [];

    apply(request: IMyBetsListRequest): IMyBetsListRequest {
        let result = request;
        for (const constraint of this.constraints) {
            result = constraint.reduce(result);
        }

        return result;
    }

    clean(): void {
        this.constraints.length = 0;
    }

    addConstraint(constraint: IFilterConstraint<IMyBetsListRequest>): void {
        if (this.constraints.some((c) => c.constructor === constraint.constructor)) {
            this.removeConstraint(constraint);
        }
        this.constraints.push(constraint);
    }

    removeConstraint(constraint: IFilterConstraint<IMyBetsListRequest> | Type<any>): void {
        if (typeof constraint === 'function') {
            const instance = this.getConstraint(constraint);
            if (instance) {
                this.removeConstraint(instance);
            }
        }
        let index = -1;
        for (let i = 0; i < this.constraints.length; i++) {
            if (this.constraints[i].constructor === constraint.constructor) {
                index = i;
            }
        }
        if (index > -1) {
            this.constraints.splice(index, 1);
        }
    }

    getConstraint<TConstraintType extends IFilterConstraint<IMyBetsListRequest>>(type: Type<TConstraintType>): TConstraintType | undefined {
        for (const constraint of this.constraints) {
            if (constraint.constructor === type) {
                return constraint as TConstraintType;
            }
        }

        return undefined;
    }
}
